import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Autocomplete, TextField, Typography, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { mutate } from 'swr';
import { useGetEntityNetwork, useCreateEntity } from '../../api/entities';
import { updateAccount } from '../../api/accounts';
import { formatPercentage } from '../../utils/formatters';
import SnackbarContext from '../../context/snackbar-context';
import styles from './AccountTitleEditDialog.module.css';

const ENTITY_KINDS = ['Person', 'Business', 'Trust', 'Estate'];
const ACCOUNTS_CACHE_KEY = 'ACCOUNT_BALANCES_BY_CATEGORY';

// Optimistically set an account's title in the balances-by-category cache.
// Structure-agnostic recursive patch: any node carrying { id === accountId } and
// a `title` field (a top-level account OR an associated liability) gets the new
// title — so it survives changes to the cache's category nesting.
function applyOptimisticTitle(data, accountId, newTitle) {
  const walk = (node) => {
    if (Array.isArray(node)) return node.map(walk);
    if (node && typeof node === 'object') {
      const next = {};
      for (const [k, v] of Object.entries(node)) next[k] = walk(v);
      if (next.id === accountId && 'title' in next) next.title = newTitle;
      return next;
    }
    return node;
  };
  return walk(data);
}

// Account-ownership Step 4: set an account's TITLE — whose name is on the account —
// as one or more entities with a percentage split (cap-table Σ ≤ 100%), replacing
// the legacy free-text account_holder. Whole-number percents in the form → 0-1
// fractions to the API (PATCH /accounts/{id} {title:[...]} → set_account_title).
// Mirrors SourceEditDialog (Phase 2). A needed entity that isn't in the list yet
// can be created inline — reusing the entity page's useCreateEntity().
//
// `cacheKey` is the SWR key of the account list the caller reads from — the
// optimistic patch below is structure-agnostic, so any cache holding a node with
// this account's `id` and a `title` field works. Defaults to the Manage MBS
// Accounts cache key (this dialog's original, and still primary, caller).
export default function AccountTitleEditDialog({ open, onClose, onSaved, account, cacheKey = ACCOUNTS_CACHE_KEY }) {
  const accountId = account?.id;
  const accountName = account?._displayName || account?.account_holder || 'this account';
  const { data: network, isLoading: entitiesLoading } = useGetEntityNetwork(open);
  const entities = useMemo(() => network?.entities || [], [network]);
  // The pick-list = the network entities PLUS any of this account's current title
  // entities not yet in the network. Seeding from title[] (which already carries
  // display_name/kind) means a pre-existing title renders "John Smith (100%)"
  // instantly instead of a misleading "Entity" while the network loads — and the
  // current value always matches an option (no MUI "value not in options" warning).
  const entityOptions = useMemo(() => {
    const byId = new Map(entities.map((e) => [e.id, e]));
    for (const t of (account?.title || [])) {
      if (t?.entity_id && !byId.has(t.entity_id)) {
        byId.set(t.entity_id, { id: t.entity_id, display_name: t.display_name, kind: t.kind });
      }
    }
    return Array.from(byId.values());
  }, [entities, account]);
  const entitiesById = useMemo(() => Object.fromEntries(entityOptions.map((e) => [e.id, e])), [entityOptions]);
  const createEntity = useCreateEntity();
  const { openSnackbarError } = useContext(SnackbarContext) || {};

  const [rows, setRows] = useState([]); // [{ entity_id, pct }] — pct is whole-number 0-100
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Inline entity creation
  const [creating, setCreating] = useState(false);
  const [newKind, setNewKind] = useState('Business');
  const [newName, setNewName] = useState('');

  // Seed rows from the account's current title[] when the dialog opens.
  useEffect(() => {
    if (!open) return;
    const title = account?.title || [];
    setRows(title.length
      ? title.map((t) => ({ entity_id: t.entity_id, pct: Math.round((Number(t.pct) || 0) * 100) }))
      : [{ entity_id: '', pct: '' }]);
    setErr(null);
    setCreating(false); setNewName(''); setNewKind('Business');
  }, [open, account]);

  const totalPct = rows.reduce((s, r) => s + (Number(r.pct) || 0), 0);
  const untitled = Math.max(0, 100 - totalPct);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { entity_id: '', pct: '' }]);
  const removeRow = (i) => setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : [{ entity_id: '', pct: '' }]));

  const validationError = () => {
    if (totalPct > 100.0001) return 'Title can’t exceed 100%.';
    const ids = rows.filter((r) => r.entity_id).map((r) => r.entity_id);
    if (new Set(ids).size !== ids.length) return 'An entity is listed more than once.';
    for (const r of rows) {
      if (r.entity_id && !(Number(r.pct) > 0)) return 'Each titled entity needs a percent greater than 0.';
    }
    return null;
  };

  // Create a new entity inline and drop it into the first empty row (else append).
  const handleCreateEntity = async () => {
    const name = newName.trim();
    if (!name) { setErr('Enter a name for the new entity.'); return; }
    setBusy(true); setErr(null);
    try {
      const created = await createEntity({ kind: newKind, display_name: name, name });
      const id = created?.id;
      if (id) {
        setRows((rs) => {
          const emptyIdx = rs.findIndex((r) => !r.entity_id);
          if (emptyIdx >= 0) return rs.map((r, i) => (i === emptyIdx ? { ...r, entity_id: id } : r));
          return [...rs, { entity_id: id, pct: '' }];
        });
      }
      setCreating(false); setNewName('');
    } catch (e) {
      setErr((e && (e.message || e.toString())) || 'Couldn’t create the entity.');
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    const v = validationError();
    if (v) { setErr(v); return; }
    const title = rows
      .filter((r) => r.entity_id && Number(r.pct) > 0)
      .map((r) => ({ entity_id: r.entity_id, pct: Number(r.pct) / 100 }));
    // The shape the MBS/form render from: display_name/kind + needs_review:false,
    // mirroring what set_account_title persists once a human confirms the title.
    const optimisticTitle = title.map((t) => ({
      ...t,
      needs_review: false,
      display_name: entitiesById[t.entity_id]?.display_name,
      kind: entitiesById[t.entity_id]?.kind,
    }));
    // Close immediately — the change is applied optimistically to the shared cache
    // and persisted in the background; a failed save rolls back + toasts (no hang).
    onClose();
    try {
      await mutate(
        cacheKey,
        updateAccount(accountId, { title }), // empty title clears the set
        {
          optimisticData: (current) => applyOptimisticTitle(current, accountId, optimisticTitle),
          rollbackOnError: true,
          revalidate: true,
          populateCache: false,
        },
      );
      onSaved?.();
    } catch (e) {
      openSnackbarError?.('Couldn’t save the account title — your change was reverted.');
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Title — {accountName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" className={styles.intro}>
          Whose name is on this account? Pick one or more entities and their share — any
          remainder is left untitled.
        </Typography>

        {rows.map((r, i) => (
          <div key={i} className={styles.row}>
            <Autocomplete
              className={styles.entity}
              options={entityOptions}
              loading={entitiesLoading}
              getOptionLabel={(o) => (typeof o === 'string' ? o : (o.display_name || ''))}
              isOptionEqualToValue={(o, v) => o.id === (v?.id || v)}
              value={entitiesById[r.entity_id] || null}
              onChange={(e, val) => setRow(i, { entity_id: val?.id || '' })}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id}>
                  {o.display_name}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{o.kind}</Typography>
                </MenuItem>
              )}
              renderInput={(params) => <TextField {...params} label="Entity" placeholder="Select or search…" size="small" />}
            />
            <TextField
              className={styles.pct}
              label="%" type="number" size="small"
              value={r.pct}
              onChange={(e) => setRow(i, { pct: e.target.value })}
              inputProps={{ min: 0, max: 100 }}
            />
            <IconButton aria-label="Remove entity" size="small" className={styles.removeBtn} onClick={() => removeRow(i)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}

        <div className={styles.addActions}>
          <Button startIcon={<AddIcon />} size="small" onClick={addRow}>Add entity</Button>
          {!creating && <Button size="small" onClick={() => { setCreating(true); setErr(null); }}>Create new entity…</Button>}
        </div>

        {creating && (
          <div className={styles.createBox}>
            <FormControl size="small" className={styles.createKind}>
              <InputLabel id="new-entity-kind">Kind</InputLabel>
              <Select labelId="new-entity-kind" label="Kind" value={newKind} onChange={(e) => setNewKind(e.target.value)}>
                {ENTITY_KINDS.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              className={styles.createName}
              label="Name" size="small"
              value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="Legal / display name"
            />
            <Button size="small" variant="outlined" onClick={handleCreateEntity} disabled={busy}>Create</Button>
            <Button size="small" onClick={() => { setCreating(false); setNewName(''); }}>Cancel</Button>
          </div>
        )}

        <div className={styles.footer}>
          <Typography variant="body2">Titled: {formatPercentage(totalPct / 100)}</Typography>
          <Typography variant="body2" color="text.secondary">Untitled: {formatPercentage(untitled / 100)}</Typography>
        </div>
        {err && <Typography variant="body2" color="error" className={styles.error}>{err}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={busy}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
